import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Smartphone,
    BarChart3,
    Receipt,
    Globe,
    Share2,
    CreditCard,
    Users,
    Building2,
    Sparkles,
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    CheckCircle2
} from "lucide-react";

export const IMPORTANT_PAGES = [
    {
        id: "mobile-billing",
        title: "Mobile Invoicing & WhatsApp",
        subtitle: "Instant WhatsApp Bills & Auto Dues Collection",
        desc: "Create bills on your phone in 30s. Send 1-click WhatsApp invoices with payment links and automated reminders.",
        path: "/features/mobile-billing",
        icon: <Smartphone className="w-7 h-7 text-emerald-400" />,
        badge: "Hot • WhatsApp API",
        gradient: "from-emerald-600 via-teal-600 to-cyan-700",
        stats: "98% Open Rate • 3x Faster Dues",
        highlights: ["WhatsApp & Email Invoices", "Automated Dues Reminders", "Instant Online Payments"]
    },
    {
        id: "accounting",
        title: "Smart AI Accounting",
        subtitle: "Next-Gen Bookkeeping & Tax Filing",
        desc: "Automate your daily accounting, bank reconciliation, and real-time P&L reporting without spreadsheets.",
        path: "/features/accounting",
        icon: <BarChart3 className="w-7 h-7 text-blue-400" />,
        badge: "Core Suite",
        gradient: "from-blue-600 via-indigo-600 to-cyan-700",
        stats: "+24% Cash Flow • 100% Tax Compliant",
        highlights: ["Sales Tax & VAT Ready", "AI Bank Reconciliation", "Real-Time Balance Sheets"]
    },
    {
        id: "expenses",
        title: "AI Expense Tracker",
        subtitle: "Receipt OCR & Smart Reimbursements",
        desc: "Snap receipts from your camera. AI automatically reads amounts, tax rates, and categorizes every expense.",
        path: "/features/expenses",
        icon: <Receipt className="w-7 h-7 text-amber-400" />,
        badge: "Smart Scan",
        gradient: "from-amber-600 via-orange-600 to-rose-700",
        stats: "Save 15+ Hours/mo • 99.9% OCR Accuracy",
        highlights: ["Instant Camera OCR Scan", "Auto Tax Categorization", "Team Expense Tracking"]
    },
    {
        id: "websites",
        title: "No-Code Website Builder",
        subtitle: "Launch Your Business Site in 60s",
        desc: "Build stunning, mobile-responsive websites with AI copywriting, custom domains, and pre-built templates.",
        path: "/features/websites",
        icon: <Globe className="w-7 h-7 text-purple-400" />,
        badge: "Zero Code",
        gradient: "from-purple-600 via-violet-600 to-indigo-700",
        stats: "98/100 SEO Score • 100+ Templates",
        highlights: ["Visual Drag & Drop Editor", "AI Copywriting Engine", "Free Built-in Hosting"]
    },
    {
        id: "social",
        title: "AI Social Media Autopilot",
        subtitle: "Multi-Channel Viral Content Growth",
        desc: "Schedule posts, generate high-converting captions, and grow across Instagram, LinkedIn, and Facebook automatically.",
        path: "/features/social",
        icon: <Share2 className="w-7 h-7 text-pink-400" />,
        badge: "Viral Growth",
        gradient: "from-pink-600 via-rose-600 to-red-700",
        stats: "+45% Engagement • 6 Platforms",
        highlights: ["Auto AI Caption Generator", "Multi-Platform Scheduler", "Engagement Analytics"]
    },
    {
        id: "pos",
        title: "Unified Retail POS",
        subtitle: "Omnichannel Store & Inventory Sync",
        desc: "Connect your physical shop and digital store in real-time with ultra-fast barcode scanning and stock sync.",
        path: "/features/pos",
        icon: <CreditCard className="w-7 h-7 text-cyan-400" />,
        badge: "Retail & Stores",
        gradient: "from-cyan-600 via-blue-600 to-indigo-700",
        stats: "< 0.1s Real-Time Sync",
        highlights: ["Omnichannel Stock Sync", "Barcode & Card Billing", "Staff Sales Tracking"]
    },
    {
        id: "payroll",
        title: "Automated Payroll",
        subtitle: "1-Click Salary & Tax Compliance",
        desc: "Distribute employee salaries, calculate statutory taxes, and generate downloadable salary slips in seconds.",
        path: "/features/payroll",
        icon: <Users className="w-7 h-7 text-teal-400" />,
        badge: "HR & Salaries",
        gradient: "from-teal-600 via-emerald-600 to-green-700",
        stats: "100% Tax Accuracy • Direct Deposits",
        highlights: ["Auto TDS & PF Deduction", "Direct Bank Payouts", "Instant Payslips"]
    },
    {
        id: "enterprise",
        title: "Enterprise Custom ERP",
        subtitle: "Dedicated Architects & Top 1% Devs",
        desc: "Tailored ERP integrations, dedicated Technical Account Managers, and custom AI trained on your private data.",
        path: "/enterprise",
        icon: <Building2 className="w-7 h-7 text-blue-300" />,
        badge: "Enterprise",
        gradient: "from-slate-800 via-slate-900 to-blue-950",
        stats: "99.99% Bank-Grade Uptime",
        highlights: ["Custom Model Training", "Multi-Entity Subsidiaries", "Dedicated Account Manager"]
    },
    {
        id: "pricing",
        title: "Transparent Pricing",
        subtitle: "Affordable Growth for All Businesses",
        desc: "Start with a 14-day free trial. Choose flexible monthly or discounted annual billing with cancel-anytime freedom.",
        path: "/pricing",
        icon: <Sparkles className="w-7 h-7 text-amber-300" />,
        badge: "14-Day Free Trial",
        gradient: "from-blue-600 via-indigo-600 to-emerald-600",
        stats: "Zero Setup Fee • Cancel Anytime",
        highlights: ["14-Day Free Access", "No Credit Card Required", "24/7 Dedicated Support"]
    }
];

export default function AppSlider() {
    const [currentIndex, setCurrentIndex] = useState(0);
    const total = IMPORTANT_PAGES.length;

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % total);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + total) % total);
    };

    // Continuous automatic sliding every 3.5s
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % total);
        }, 3500);

        return () => clearInterval(timer);
    }, [currentIndex, total]);

    const activePage = IMPORTANT_PAGES[currentIndex];

    return (
        <div className="w-full py-8 md:py-14 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Featured Active Slide Card with Floating Controls */}
                <div className="relative group">
                    {/* Floating Navigation Controls (Desktop Side Arrows) */}
                    <button
                        onClick={prevSlide}
                        className="hidden md:flex absolute -left-5 top-1/2 -translate-y-1/2 z-30 p-3.5 rounded-full bg-slate-900/80 hover:bg-slate-900 border border-white/20 text-white backdrop-blur-md shadow-xl transition-all active:scale-90 cursor-pointer items-center justify-center hover:scale-110"
                        aria-label="Previous Slide"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={nextSlide}
                        className="hidden md:flex absolute -right-5 top-1/2 -translate-y-1/2 z-30 p-3.5 rounded-full bg-slate-900/80 hover:bg-slate-900 border border-white/20 text-white backdrop-blur-md shadow-xl transition-all active:scale-90 cursor-pointer items-center justify-center hover:scale-110"
                        aria-label="Next Slide"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activePage.id}
                            initial={{ opacity: 0, x: 40, scale: 0.98 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: -40, scale: 0.98 }}
                            transition={{ duration: 0.4, ease: "easeInOut" }}
                            className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-900 to-slate-950 text-white shadow-2xl border border-slate-800"
                        >
                            {/* Colorful Glow Backdrop */}
                            <div className={`absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br ${activePage.gradient} opacity-25 blur-[120px] pointer-events-none rounded-full`}></div>
                            <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-blue-500/10 blur-[100px] pointer-events-none rounded-full"></div>

                            <div className="relative z-10 p-6 sm:p-10 md:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                                {/* Left Content */}
                                <div className="lg:col-span-7 space-y-6">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="p-3.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 shadow-inner">
                                            {activePage.icon}
                                        </div>
                                        <span className="px-3.5 py-1.5 rounded-full bg-white/15 border border-white/20 text-white text-xs font-bold tracking-wider uppercase backdrop-blur-md">
                                            {activePage.badge}
                                        </span>
                                        <span className="text-xs font-semibold text-emerald-400 bg-emerald-950/60 border border-emerald-500/30 px-3 py-1 rounded-full">
                                            {activePage.stats}
                                        </span>
                                    </div>

                                    <div>
                                        <h3 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight leading-tight">
                                            {activePage.title}
                                        </h3>
                                        <p className="text-blue-200 text-sm sm:text-base font-semibold mt-2">
                                            {activePage.subtitle}
                                        </p>
                                    </div>

                                    <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl font-medium">
                                        {activePage.desc}
                                    </p>

                                    {/* Highlights Pills */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-2">
                                        {activePage.highlights.map((hl, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-white/10 text-xs font-semibold text-slate-200"
                                            >
                                                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                                                <span className="truncate">{hl}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* CTAs */}
                                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                                        <Link
                                            to={activePage.path}
                                            className="px-8 py-4 rounded-full bg-white text-slate-900 font-extrabold text-base hover:bg-slate-100 hover:shadow-xl transition-all flex items-center justify-center gap-2 shadow-lg group active:scale-[0.98]"
                                        >
                                            Explore Feature <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                        <Link
                                            to="/signup"
                                            className="px-8 py-4 rounded-full bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500 hover:from-blue-500 hover:to-emerald-400 text-white font-extrabold text-base transition-all flex items-center justify-center shadow-lg shadow-blue-600/30 active:scale-[0.98]"
                                        >
                                            Start Free Trial ✨
                                        </Link>
                                    </div>
                                </div>

                                {/* Right: Visual Preview Card */}
                                <div className="lg:col-span-5 relative">
                                    <div className="relative rounded-3xl bg-slate-900/90 border border-slate-700/80 p-6 shadow-2xl backdrop-blur-xl space-y-4 overflow-hidden">
                                        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                                            <div className="flex gap-2">
                                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                                            </div>
                                            <span className="text-[11px] font-mono text-slate-400">blinqbooks.com{activePage.path}</span>
                                        </div>

                                        <div className="p-4 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/5 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-white uppercase tracking-wider">Live Module</span>
                                                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                                            </div>
                                            <h4 className="text-lg font-bold text-white">{activePage.title}</h4>
                                            <p className="text-xs text-slate-400 leading-relaxed">{activePage.desc}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 pt-1">
                                            <div className="p-3 rounded-xl bg-white/5 border border-white/5 text-center">
                                                <div className="text-lg font-black text-emerald-400">Instant</div>
                                                <div className="text-[10px] text-slate-400 uppercase font-semibold">Deployment</div>
                                            </div>
                                            <div className="p-3 rounded-xl bg-white/5 border border-white/5 text-center">
                                                <div className="text-lg font-black text-cyan-400">AI-Driven</div>
                                                <div className="text-[10px] text-slate-400 uppercase font-semibold">Workflow</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Sleek Minimal Indicator Dots & Mobile Nav Controls */}
                <div className="mt-6 sm:mt-8 flex items-center justify-center gap-3">
                    <button
                        onClick={prevSlide}
                        className="md:hidden p-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 shadow-sm active:scale-95 cursor-pointer"
                        aria-label="Previous Slide"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <div className="flex items-center gap-2">
                        {IMPORTANT_PAGES.map((page, idx) => (
                            <button
                                key={page.id}
                                onClick={() => setCurrentIndex(idx)}
                                className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                                    currentIndex === idx
                                        ? "w-8 bg-emerald-500 shadow-sm shadow-emerald-500/50"
                                        : "w-2 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-600"
                                }`}
                                aria-label={`Go to slide ${idx + 1}`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={nextSlide}
                        className="md:hidden p-2 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 shadow-sm active:scale-95 cursor-pointer"
                        aria-label="Next Slide"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
